// Reset del estado BLE de ESTE equipo cuando el comercio salió de la feature (SPEC-2).
//
// DECISIÓN DE PRODUCTO (2026-06-10): "Quitar la impresión Bluetooth" = RESET TOTAL, no
// archivo. Archivar restauraría config stale (la clase de bug de #1020/#1050), el deviceId
// de Web Bluetooth es volátil (re-emparejar es inevitable) y la velocidad calibrada es por
// impresora FÍSICA (restaurarla sobre otra imprime basura). Al regresar, el flujo de alta
// parte de defaults seguros.
//
// Qué se borra: master toggle (off) + perfiles de impresoras (webBlePrintersConfig, vía el
// reducer reset del slice para que la UI viva reaccione) + overrides de debug de transporte/
// raster + logo cacheado + avisos legales editados (webBleLegalDisclaimer:*) + `device.forget()`
// best-effort (revoca el permiso del navegador donde la API exista). DECISIÓN (2026-06-11):
// la conservación de estado aplica SOLO al apagado del master toggle — "Quitar" es borrón total.
//
// Idempotente y best-effort por construcción: corre N veces sin efectos nuevos; cada paso
// va en try/catch independiente (un fallo no aborta el resto; la próxima reconciliación
// converge). NUNCA lanza.

import { appStore } from '@store/index';
import { webBlePrintersSlice } from '@store/slices/webBlePrinters';
import { getWebBlePrintMode, setWebBlePrintMode } from './printMode';
import { isWebBleSupported } from './bleTransport';

// Overrides de debug del transporte/raster (panel debug) — estado de calibración del equipo.
const BLE_DEBUG_KEYS = ['webBleChunkSize', 'webBleChunkDelay', 'webBleRasterMode', 'webBleRasterBandHeight'];
// Logo del comercio cacheado para el raster (se re-fetchea al regresar).
const BLE_LOGO_CACHE_KEY = 'webBleCommerceLogo';
// Avisos legales del ticket (per-comercio en este equipo): `webBleLegalDisclaimer:<id>`.
const BLE_LEGAL_PREFIX = 'webBleLegalDisclaimer:';

const lsGet = (key: string): string | null => {
  try {
    return typeof window !== 'undefined' ? (window.localStorage?.getItem(key) ?? null) : null;
  } catch {
    return null;
  }
};

const lsRemove = (key: string): void => {
  try {
    if (typeof window !== 'undefined') window.localStorage?.removeItem(key);
  } catch {
    /* no-op */
  }
};

// ¿Este equipo tiene estado BLE activo que resetear? (guard del reconciliador: en la inmensa
// mayoría de los equipos —control sin haber usado BT— esto es false y el reset es no-op puro).
export const hasActiveBleDeviceState = (): boolean => {
  if (getWebBlePrintMode()) return true;
  const config = lsGet('webBlePrintersConfig');
  if (config && config !== '{}') return true;
  return BLE_DEBUG_KEYS.some((k) => lsGet(k) !== null);
};

// `forget()` best-effort: revoca el permiso del navegador sobre los dispositivos autorizados.
// Requiere getDevices (tras flag en Chrome) y forget (Chrome 110+); si no existen, no-op —
// el permiso huérfano es inofensivo (sin config, nada lo usa).
const forgetAuthorizedDevices = async (): Promise<void> => {
  try {
    if (!isWebBleSupported()) return;
    const bt = navigator.bluetooth;
    if (!bt || typeof bt.getDevices !== 'function') return;
    // `forget()` no está en nuestros tipos de web-bluetooth (Chrome 110+): acceso defensivo.
    const devices = (await bt.getDevices()) as Array<{ forget?: () => Promise<void> }>;
    await Promise.allSettled(devices.map((d) => (typeof d.forget === 'function' ? d.forget() : undefined)));
  } catch {
    /* best-effort */
  }
};

// `forget()` quirúrgico al ELIMINAR un perfil de impresora: revoca el permiso del navegador
// solo sobre el dispositivo de ESE perfil, para no dejar una "impresora fantasma" autorizada
// (getDevices la seguiría listando y la reconexión podría tomarla al agregar otra). NO toca
// dispositivos que otro perfil restante siga referenciando (caja/cocina pueden compartir
// impresora física). Best-effort: sin getDevices/forget es no-op (permiso huérfano inofensivo).
// Nota Deshacer (C-23): restaurar el perfil recupera su config, pero re-conectar pedirá el
// chooser una vez (el permiso ya fue revocado) — preferible al fantasma.
type PrinterDeviceRef = { deviceId?: string | null; deviceName?: string | null };

export const forgetPrinterDevice = async (
  removed: PrinterDeviceRef | null | undefined,
  remaining: PrinterDeviceRef[],
): Promise<void> => {
  try {
    if (!removed || (!removed.deviceId && !removed.deviceName)) return;
    if (!isWebBleSupported()) return;
    const bt = navigator.bluetooth;
    if (!bt || typeof bt.getDevices !== 'function') return;
    const stillInUse = (d: { id?: string; name?: string }): boolean =>
      remaining.some((p) => (p.deviceId && p.deviceId === d.id) || (p.deviceName && p.deviceName === d.name));
    const matchesRemoved = (d: { id?: string; name?: string }): boolean =>
      Boolean(removed.deviceId && removed.deviceId === d.id) ||
      Boolean(removed.deviceName && removed.deviceName === d.name);
    const devices = (await bt.getDevices()) as Array<{
      id?: string;
      name?: string;
      forget?: () => Promise<void>;
    }>;
    await Promise.allSettled(
      devices
        .filter((d) => matchesRemoved(d) && !stillInUse(d))
        .map((d) => (typeof d.forget === 'function' ? d.forget() : undefined)),
    );
  } catch {
    /* best-effort */
  }
};

// Reset total del estado BLE del equipo. Fire-and-forget (la parte async es solo forget()).
export const resetBleDeviceState = (origin: 'revert_click' | 'reconciler'): void => {
  try {
    setWebBlePrintMode(false);
  } catch {
    /* no-op */
  }
  try {
    // Vía el reducer (no removeItem directo): persiste el mapa vacío Y notifica a la UI viva.
    appStore.dispatch(webBlePrintersSlice.actions.reset());
  } catch {
    lsRemove('webBlePrintersConfig');
  }
  BLE_DEBUG_KEYS.forEach(lsRemove);
  lsRemove(BLE_LOGO_CACHE_KEY);
  // Borrón total: también los avisos legales (todas las llaves del prefijo en este equipo).
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const legalKeys: string[] = [];
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const key = window.localStorage.key(i);
        if (key && key.startsWith(BLE_LEGAL_PREFIX)) legalKeys.push(key);
      }
      legalKeys.forEach(lsRemove);
    }
  } catch {
    /* no-op */
  }
  forgetAuthorizedDevices().catch(() => undefined);
  // Miga de diagnóstico para soporte (no telemetría): cuándo y por qué se reseteó este equipo.
  try {
    if (typeof window !== 'undefined') {
      window.localStorage?.setItem('webBleLastReset', JSON.stringify({ origin, at: Date.now() }));
    }
  } catch {
    /* no-op */
  }
};
